import { useEffect, useState } from 'react';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import TrustedBy from './components/TrustedBy';
import About from './components/About';
import Services from './components/Services';
import Reviews from './components/Reviews';
import Gallery from './components/Gallery';
import ServiceAreas from './components/ServiceAreas';
import FAQ from './components/FAQ';
import Contact from './components/Contact';
import Footer from './components/Footer';
import SEO from '../../components/feature/SEO';

const siteUrl = (import.meta.env.VITE_SITE_URL as string | undefined) ?? '';

const HOME_JSON_LD = [
  {
    '@context': 'https://schema.org',
    '@type': 'ElectricalContractor',
    '@id': `${siteUrl}/#business`,
    name: 'Quantum Electrical Services',
    url: siteUrl || '/',
    telephone: '+441529265060',
    email: 'info@quantum-electrical.uk',
    description:
      'NAPIT registered electricians in Sleaford, Lincolnshire. Full rewires, fuse board upgrades, EV chargers, lighting, fault finding, PAT testing, EICR reports and more. Free no-obligation quotes.',
    priceRange: '££',
    image: 'https://storage.readdy-site.link/project_files/00423dcc-69f9-4748-ac0c-c15d54e5586b/90758309-4e57-4704-ada6-1299ec143e1f_3eb45589-c339-4fd1-9e2d-32cca06dacf9-removebg-preview.png',
    logo: 'https://storage.readdy-site.link/project_files/00423dcc-69f9-4748-ac0c-c15d54e5586b/90758309-4e57-4704-ada6-1299ec143e1f_3eb45589-c339-4fd1-9e2d-32cca06dacf9-removebg-preview.png',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '2-7 St Vincents',
      addressLocality: 'Sleaford',
      addressRegion: 'Lincolnshire',
      postalCode: 'NG34',
      addressCountry: 'GB',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 53.0044,
      longitude: -0.4256,
    },
    areaServed: [
      'Sleaford', 'Grantham', 'Boston', 'Spalding', 'Lincoln',
      'Newark-on-Trent', 'Stamford', 'Bourne', 'Heckington', 'Ruskington',
      'Ancaster', 'Cranwell', 'Leadenham', 'Navenby', 'Metheringham',
    ],
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        opens: '08:00',
        closes: '18:00',
      },
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Saturday'],
        opens: '09:00',
        closes: '16:00',
      },
    ],
    hasCredential: ['NAPIT Registered', 'Part P Certified', 'OZEV Approved', '18th Edition IET'],
    sameAs: [],
  },
  {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${siteUrl}/#website`,
    url: siteUrl || '/',
    name: 'Quantum Electrical Services Sleaford',
    description: 'NAPIT registered electricians in Sleaford, Lincolnshire',
    publisher: { '@id': `${siteUrl}/#business` },
    inLanguage: 'en-GB',
  },
];

function LoadingScreen({ onDone }: { onDone: () => void }) {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setProgress((p) => {
        if (p >= 100) {
          clearInterval(interval);
          setTimeout(onDone, 300);
          return 100;
        }
        return p + Math.random() * 18 + 5;
      });
    }, 100);
    return () => clearInterval(interval);
  }, [onDone]);

  return (
    <div className="fixed inset-0 bg-[#0A1628] z-50 flex flex-col items-center justify-center">
      {/* Logo */}
      <div className="relative mb-8">
        <div
          className="absolute inset-0 rounded-full blur-2xl"
          style={{
            background: 'radial-gradient(circle, rgba(200,169,106,0.3), transparent)',
            animation: 'pulseGlow 1.5s ease-in-out infinite',
          }}
        />
        <img
          src="https://storage.readdy-site.link/project_files/00423dcc-69f9-4748-ac0c-c15d54e5586b/90758309-4e57-4704-ada6-1299ec143e1f_3eb45589-c339-4fd1-9e2d-32cca06dacf9-removebg-preview.png?v=2a77e748ecd680d6c594c6350bb285eb"
          alt="Quantum Electrical Services"
          className="w-28 h-28 object-contain relative z-10"
          style={{
            filter: 'drop-shadow(0 0 24px rgba(200,169,106,0.8)) drop-shadow(0 0 48px rgba(200,169,106,0.4))',
            animation: 'slowSpin 4s linear infinite',
          }}
        />
      </div>

      <p className="font-playfair text-2xl text-white mb-1 tracking-wide">Quantum Electrical</p>
      <p className="text-[#C8A96A]/70 text-xs tracking-widest uppercase mb-8">Services · Sleaford</p>

      {/* Progress bar */}
      <div className="w-56 h-0.5 bg-white/10 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-150"
          style={{
            width: `${Math.min(progress, 100)}%`,
            background: 'linear-gradient(90deg, #A8894F, #C8A96A, #E0C78A)',
            boxShadow: '0 0 12px rgba(200,169,106,0.8)',
          }}
        />
      </div>
      <p className="text-white/30 text-xs mt-3 tabular-nums">{Math.min(Math.round(progress), 100)}%</p>
    </div>
  );
}

export default function Home() {
  const [loading, setLoading] = useState(true);
  const [fadeOut, setFadeOut] = useState(false);

  const handleDone = () => {
    setFadeOut(true);
    setTimeout(() => setLoading(false), 500);
  };

  return (
    <>
      <SEO
        title="Quantum Electrical Services Sleaford | NAPIT Electrician Lincolnshire"
        description="Quantum Electrical Services - NAPIT registered electricians in Sleaford, Lincolnshire. Full rewires, fuse board upgrades, EV chargers, lighting, fault finding. Free no-obligation quotes. Call 01529 265060."
        keywords="electrician Sleaford, electrical services Lincolnshire, NAPIT electrician, EV charger installation, rewire Sleaford"
        canonicalPath="/"
        jsonLd={HOME_JSON_LD}
      />
      {loading && (
        <div className={`transition-opacity duration-500 ${fadeOut ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}>
          <LoadingScreen onDone={handleDone} />
        </div>
      )}

      <div className={`transition-opacity duration-700 ${loading ? 'opacity-0' : 'opacity-100'}`}>
        <Navbar />
        <main>
          <Hero />
          <TrustedBy />
          <About />
          <Services />
          <Reviews />
          <Gallery />
          <ServiceAreas />
          <FAQ />
          <Contact />
        </main>
        <Footer />
      </div>
    </>
  );
}
